import { useEffect, useState } from "react";
import { getUploadToSummarizeAverage } from "../../utils/functions";

export default function UploadToSummarizeWidget() {
  const [metric, setMetric] = useState({
    averageDurationSeconds: null,
    caseCount: 0,
    completedCount: 0,
    incompleteCount: 0,
    cases: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const fetchMetric = async () => {
      try {
        const data = await getUploadToSummarizeAverage();
        if (!data) throw new Error("Turnaround metric was unavailable");
        setMetric({
          averageDurationSeconds:
            typeof data.averageDurationSeconds === "number"
              ? data.averageDurationSeconds
              : null,
          caseCount: typeof data.caseCount === "number" ? data.caseCount : 0,
          completedCount:
            typeof data.completedCount === "number" ? data.completedCount : 0,
          incompleteCount:
            typeof data.incompleteCount === "number" ? data.incompleteCount : 0,
          cases: Array.isArray(data.cases) ? data.cases : [],
        });
        setHasError(false);
      } catch (error) {
        console.error("Error loading upload-to-summarize average:", error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetric();
  }, []);

  useEffect(() => {
    if (!showDetails) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowDetails(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showDetails]);

  const formatTimestamp = (timestamp) => {
    if (typeof timestamp !== "number") return "—";
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShowDetails(true)}
        className="shadow-knowtex-shadow p-4 rounded-md w-[20%] flex flex-col items-center justify-center text-left hover:bg-gray-50 transition-colors"
        title="Click to view turnaround case details"
        disabled={isLoading || hasError}
      >
        <h4 className="font-semibold text-ktx-navy text-center">
          Upload → Summarize
        </h4>
        {isLoading ? (
          <div className="w-6 h-6 border-2 border-dashed rounded-full animate-spin border-ktx-purple" />
        ) : hasError ? (
          <h3 className="text-red-500 text-[18px] font-semibold">N/A</h3>
        ) : (
          <>
            <h3 className="text-sage-green text-[24px] font-semibold">
              {metric.averageDurationSeconds === null
                ? "N/A"
                : `${metric.averageDurationSeconds.toFixed(2)}s`}
            </h3>
            <p className="text-xs text-neutral-500 mt-1">
              {metric.caseCount} cases
            </p>
            <p className="text-xs text-neutral-500">
              {metric.completedCount} complete / {metric.incompleteCount} incomplete
            </p>
          </>
        )}
      </button>

      {showDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-20 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-4/5 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <div>
                <h2 className="text-xl font-bold text-dark-purple">
                  Upload → Summarize Turnaround
                </h2>
                <p className="text-gray-600 text-sm">
                  {metric.caseCount} upload windows analyzed
                </p>
              </div>
              <button
                type="button"
                aria-label="Close turnaround details"
                onClick={() => setShowDetails(false)}
                className="bg-transparent text-gray-500 hover:text-gray-800 p-2 rounded-full hover:bg-gray-100"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-sm text-gray-500">Average Duration</div>
                <div className="text-2xl font-bold text-ktx-purple">
                  {metric.averageDurationSeconds === null
                    ? "N/A"
                    : `${metric.averageDurationSeconds.toFixed(2)}s`}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-sm text-gray-500">Complete</div>
                <div className="text-2xl font-bold text-green-600">
                  {metric.completedCount}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-sm text-gray-500">Incomplete</div>
                <div className="text-2xl font-bold text-orange-600">
                  {metric.incompleteCount}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-dark-purple text-white">
                    <th className="px-4 py-2 text-left">#</th>
                    <th className="px-4 py-2 text-left">Patient</th>
                    <th className="px-4 py-2 text-left">Upload Start</th>
                    <th className="px-4 py-2 text-left">Summarize End</th>
                    <th className="px-4 py-2 text-left">Duration (s)</th>
                    <th className="px-4 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {metric.cases.length === 0 ? (
                    <tr>
                      <td className="border px-4 py-4 text-gray-500" colSpan={6}>
                        No cases found for this window.
                      </td>
                    </tr>
                  ) : (
                    metric.cases.map((row, index) => (
                      <tr
                        key={`${row.patientId}-${row.uploadStart}-${index}`}
                        className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
                      >
                        <td className="border px-4 py-2 font-medium">
                          {index + 1}
                        </td>
                        <td className="border px-4 py-2">
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                            {row.patientId || "—"}
                          </code>
                        </td>
                        <td className="border px-4 py-2 text-gray-600">
                          {formatTimestamp(row.uploadStart)}
                        </td>
                        <td className="border px-4 py-2 text-gray-600">
                          {formatTimestamp(row.runChatgptEnd)}
                        </td>
                        <td className="border px-4 py-2 text-gray-600">
                          {typeof row.durationMs === "number"
                            ? (row.durationMs / 1000).toFixed(2)
                            : "—"}
                        </td>
                        <td className="border px-4 py-2">
                          <span
                            className={
                              row.status === "complete"
                                ? "bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm"
                                : "bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm"
                            }
                          >
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
